import { Typography } from '@material-ui/core';
import { WarningRounded } from '@material-ui/icons';
import { Color, Column, Columns, Image, Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { useAppFormik } from 'shared/form/AppFormik';
import { FileField } from 'shared/form/FileField';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';
import { uploadScreenshotSchema } from '../../data/MoovDTO';
import {
  isPdf,
  preloadOcrWorker,
  releasePreloadedOcrWorker,
} from './mv-code-ocr/readMvCodeFromImage';
import {
  useIsCheckingScreenshotInBackground,
  useVerifyWithScreenshot,
} from './useVerifyWithScreenshot';

export const UploadScreenshotTitle = styled.span`
  font-size: 18px;
`;

interface UploadTransactionScreenshotFormValues {
  file: File | null;
}

export function useUploadTransactionScreenshotForm({
  onComplete,
  onVerified,
}: {
  onComplete: () => void;
  onVerified?: () => void;
}) {
  const isOcrEnabled = useFlag('payments_ocr_mv_code_extraction');
  const verification = useVerifyWithScreenshot();

  return useAppFormik<UploadTransactionScreenshotFormValues>({
    initialValues: { file: null },
    validationSchema: uploadScreenshotSchema,
    onSubmit: async ({ file }) => {
      const variables = { file: file as File, onVerified };

      if (isOcrEnabled) {
        // Not awaited, so the carrier can close the drawer while the check runs.
        verification.mutate(variables, { onSuccess: onComplete });
      } else {
        await verification.mutateAsync(variables, { onSuccess: onComplete });
      }

      return undefined as unknown as APIResponse;
    },
    // Errors are shown by `useVerifyWithScreenshot`.
    getErrorMessage: null,
  });
}

export function useUploadScreenshotStep({
  onBack,
  onComplete,
  onVerified,
}: {
  onBack: () => void;
  onComplete: () => void;
  onVerified?: () => void;
}) {
  const formik = useUploadTransactionScreenshotForm({ onComplete, onVerified });
  const isCheckingInBackground = useIsCheckingScreenshotInBackground();
  const [isCheckDialogOpen, setIsCheckDialogOpen] = useState(false);

  if (!isCheckingInBackground && isCheckDialogOpen) {
    setIsCheckDialogOpen(false);
  }

  const openCheckDialog = useCallback(() => setIsCheckDialogOpen(true), []);
  const closeCheckDialog = useCallback(() => setIsCheckDialogOpen(false), []);

  return {
    formik,
    isCheckingInBackground,
    isCheckDialogOpen,
    openCheckDialog,
    closeCheckDialog,
    handleBack: isCheckingInBackground ? openCheckDialog : onBack,
    submitLabel: isCheckingInBackground
      ? 'Submitting for Review…'
      : 'Submit for Review',
    isSubmitDisabled:
      !formik.values.file || formik.isSubmitting || isCheckingInBackground,
  };
}

export function UploadTransactionScreenshotFormContent() {
  const { values, errors, isSubmitting, setFieldValue } =
    useFormikContext<UploadTransactionScreenshotFormValues>();
  const isCheckingInBackground = useIsCheckingScreenshotInBackground();
  const isOcrEnabled = useFlag('payments_ocr_mv_code_extraction');

  useEffect(() => releasePreloadedOcrWorker, []);

  return (
    <Stack space="large">
      <Typography>
        If you can’t find the MV code, upload a screenshot of the $0.01
        transaction from your bank app instead.
      </Typography>

      <Image
        src="https://storage.googleapis.com/superdispatch-email-resources/payment_transaction_details.png"
        width="100%"
        alt="Transaction details example"
      />

      <Columns space="xsmall">
        <Column width="content">
          <Box position="relative" top="2px">
            <WarningRounded fontSize="small" htmlColor={Color.Yellow300} />
          </Box>
        </Column>
        <Column>
          <Typography>
            Open your bank app, and go to your transaction history. Find the{' '}
            <b>$0.01</b> transaction from Super Dispatch, and upload the
            screenshot.
          </Typography>
        </Column>
      </Columns>

      <FileField
        name="file"
        label="Upload Transaction Screenshot"
        value={values.file}
        onChange={(file) => {
          if (isOcrEnabled && file && !isPdf(file)) preloadOcrWorker();
          void setFieldValue('file', file);
        }}
        accept=".jpg, .jpeg, .png, .pdf"
        disabled={isSubmitting || isCheckingInBackground}
        error={!!errors.file}
        helperText={errors.file}
      />
    </Stack>
  );
}
